import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Loader2, UserPlus, LogOut, Settings, Lock, CreditCard } from "lucide-react";
import WaitlistButton from "@/components/shared/WaitlistButton";
import { formatPrice } from "@/utils/formatPrice";
import type { ActivityDetail } from "@/hooks/useActivityDetail";

interface ActivityActionsProps {
  activity: ActivityDetail;
  /** Canonical join charge in öre (computeActivityJoinAmountMinor), for the button price label.
   *  null = loading / non-system; >0 shows the price; ≤0/null shows no price. Never price_per_person. */
  joinChargeMinor?: number | null;
  /** 2d-ii Step 4.3: does the player have a chargeable MIT card? When a charge applies
   *  (joinChargeMinor > 0) and this is false, "Gå med" is disabled + a link to /profile is shown. */
  hasMitCard?: boolean;
  /** 2d-ii Step 4.4: has the player PAID this join? Drives the refund-leave vs disabled branch. */
  hasPaidJoin?: boolean;
  /** 2e-core Policy-modell A: is NOW inside the frozen cancellation window? Inside → paid player
   *  gets self-service refund-leave; outside → disabled + "kontakta arrangören". */
  withinCancellationWindow?: boolean;
  /** The DRAWN amount (paid row's amount_minor) — the refund display amount, one source. */
  refundChargeMinor?: number | null;
  /** 2d-iii B2: the player has an UNPAID pending join payment (accept-without-card,
   *  redirect-abandon, card decline — decision b keeps the seat). Shows the backstop CTA. */
  hasPendingJoin?: boolean;
  /** Canonical pending amount in öre for the CTA label. */
  pendingChargeMinor?: number | null;
  /** Org kill-switch: charge_enabled !== true hides the CTA (unarmed orgs feel normal). */
  chargeEnabled?: boolean;
  isOrganizer: boolean;
  hasJoined: boolean;
  isFull: boolean;
  isPast: boolean;
  isDeadlinePassed: boolean;
  onJoin: () => void;
  onLeave: () => void;
  onRefundLeave?: () => void;
  onManage: () => void;
  onCompletePayment?: () => void;
  isJoining: boolean;
  isLeaving: boolean;
  isRefundLeaving?: boolean;
  isCompletingPayment?: boolean;
}

export default function ActivityActions({
  activity,
  joinChargeMinor,
  hasMitCard = false,
  hasPaidJoin = false,
  withinCancellationWindow = false,
  refundChargeMinor,
  hasPendingJoin = false,
  pendingChargeMinor,
  chargeEnabled = false,
  isOrganizer,
  hasJoined,
  isFull,
  isPast,
  isDeadlinePassed,
  onJoin,
  onLeave,
  onRefundLeave,
  onManage,
  onCompletePayment,
  isJoining,
  isLeaving,
  isRefundLeaving = false,
  isCompletingPayment = false,
}: ActivityActionsProps) {
  const { t } = useTranslation("activities");

  // Past activity
  if (isPast) {
    return (
      <div className="bg-muted rounded-lg px-4 py-3 text-sm text-center text-muted-foreground">
        {t("detail.activityEnded")}
      </div>
    );
  }

  // Cancelled activity
  if (activity.status === "cancelled") {
    return (
      <div className="bg-red-500/10 border border-red-200 rounded-lg px-4 py-3 text-sm text-center text-red-700">
        {t("detail.activityCancelled")}
      </div>
    );
  }

  // Organizer
  if (isOrganizer) {
    return (
      <Button onClick={onManage} className="w-full gap-2">
        <Settings className="h-4 w-4" />
        {t("detail.manageActivity")}
      </Button>
    );
  }

  // Already joined
  if (hasJoined) {
    return (
      <div className="space-y-2">
        <div className="bg-primary/10 border border-primary/20 rounded-lg px-4 py-2 text-sm text-center font-medium">
          {t("detail.youAreParticipating")}
        </div>
        {/* 2d-iii B2 backstop CTA (decision b): an UNPAID pending join (accept-without-card,
            redirect-abandon, card decline) is settled HERE — the single retry surface. Hidden
            when the org's charge_enabled is false (unarmed orgs must feel normal). */}
        {hasPendingJoin && chargeEnabled && onCompletePayment && (
          <Button
            className="w-full gap-2"
            data-testid="complete-payment"
            onClick={onCompletePayment}
            disabled={isCompletingPayment}
          >
            {isCompletingPayment ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CreditCard className="h-4 w-4" />
            )}
            {pendingChargeMinor != null && pendingChargeMinor > 0
              ? `${t("detail.completePayment", { defaultValue: "Slutför betalning" })} — ${formatPrice(pendingChargeMinor / 100)}`
              : t("detail.completePayment", { defaultValue: "Slutför betalning" })}
          </Button>
        )}
        {/* 2e-core Policy-modell A — three cases for the leave button:
            - PAID + inside window  → self-service refund-leave "Lämna (återbetalas X kr)" (full
              refund + seat released via refund-activity-leave). The DRAWN amount (refundChargeMinor)
              is the single source.
            - PAID + outside window → disabled + "Betald plats — kontakta arrangören" (organizer/
              admin is the valve; admin-refund-knappen finns).
            - UNPAID                → normal atomic leave (leave_activity_v2). */}
        {hasPaidJoin ? (
          withinCancellationWindow && onRefundLeave ? (
            <Button
              variant="outline"
              size="sm"
              className="w-full gap-1 text-destructive hover:text-destructive"
              data-testid="refund-leave"
              onClick={onRefundLeave}
              disabled={isRefundLeaving}
            >
              {isRefundLeaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
              {refundChargeMinor != null && refundChargeMinor > 0
                ? `${t("detail.leaveActivity")} (${t("refundedAmount", { defaultValue: "återbetalas {{amount}}", amount: formatPrice(refundChargeMinor / 100) })})`
                : t("detail.leaveActivity")}
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                className="w-full gap-1 text-destructive hover:text-destructive"
                onClick={onLeave}
                disabled
              >
                <LogOut className="h-4 w-4" />
                {t("detail.leaveActivity")}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                {t("paidSpotContactOrganizer")}
              </p>
            </>
          )
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="w-full gap-1 text-destructive hover:text-destructive"
            onClick={onLeave}
            disabled={isLeaving}
          >
            {isLeaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
            {t("detail.leaveActivity")}
          </Button>
        )}
      </div>
    );
  }

  // Registration deadline passed
  if (isDeadlinePassed) {
    return (
      <div className="bg-muted rounded-lg px-4 py-3 text-sm text-center text-muted-foreground flex items-center justify-center gap-2">
        <Lock className="h-4 w-4" />
        {t("registrationClosed")}
      </div>
    );
  }

  // Full — show waitlist button
  if (isFull) {
    return (
      <WaitlistButton
        activityType="open_match"
        activityId={activity.id}
        isFull={true}
        allowWaitingList={activity.allow_waitlist ?? true}
        className="w-full"
      />
    );
  }

  // Available to join — show the CANONICAL join charge (computeActivityJoinAmountMinor via the hook),
  // NOT the legacy activity.price_per_person (null for fee-led activities → no/incorrect label).
  const priceLabel =
    joinChargeMinor != null && joinChargeMinor > 0
      ? ` — ${formatPrice(joinChargeMinor / 100)}`
      : "";

  // CHARGE-CORRECTNESS gate (not UX polish): a system-mode join must NEVER fire before its charge is
  // known — clicking before pricingRules load would compute the charge with empty rules → court-dropped
  // undercharge (the join mutation recomputes at click time). Disable "Gå med" until joinChargeMinor has
  // resolved (same readiness as the displayed price). Non-system needs no court recompute → always ready.
  const joinReady = activity.payment_mode !== "system" || joinChargeMinor != null;

  // 2d-ii Step 4.3: a join that will charge (joinChargeMinor > 0) requires a saved MIT card — the
  // off-session charge fires immediately after join (useActivityDetail). No card → disable "Gå med"
  // and link to /profile to save one. The detail page has NO inline add-card by design (Q2-A);
  // that lives only in the /play confirm dialog. Mirrors ActivityList's confirmWillCharge && !hasMitCard.
  // Free/0-kr joins (joinChargeMinor ≤ 0 / null) are never gated.
  const needsCard = joinChargeMinor != null && joinChargeMinor > 0 && !hasMitCard;

  return (
    <div className="space-y-2">
      <Button
        onClick={onJoin}
        disabled={isJoining || !joinReady || needsCard}
        className="w-full gap-2"
        size="lg"
      >
        {isJoining || !joinReady ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <UserPlus className="h-4 w-4" />
        )}
        {joinReady ? `${t("joinActivity")}${priceLabel}` : t("loadingPrice")}
      </Button>
      {needsCard && (
        <Link
          to="/profile"
          className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground underline hover:text-foreground"
        >
          <CreditCard className="h-3.5 w-3.5" />
          {t("saveCardToJoin")}
        </Link>
      )}
    </div>
  );
}
